<script setup>
import {onMounted, onUnmounted, reactive} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {userStore} from "@/Stores/UserStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'ListFilesComponent';

const props = defineProps({
    person: String,
    personId: String,
    claimId: String,
    clientId: String
});

const data = reactive({
    filenames: [],
    files: [],
    isEditing: false,
    loading: false,
    originalFileName: ''
});

onMounted(() => {
    getFilenames();
    fetchData();

    activeComponentStore.registerComponent(
        'ListFilesComponent',
        [
            'files'
        ],
        fetchData
    );
});

onUnmounted(() => {
    activeComponentStore.deregisterComponent('ListFilesComponent');
});

const getTimezone = (timezoneShortened) => {
    const tzName = new Date()
    return tzName.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop();
}

const fetchData = () => {
    data.loading = true;
    ApiWrapper.post("client-files", "list-files", {
        personId: props.person === 'true' ? userStore.userInfo.id : props.personId,
        claimId: props.claimId,
        clientId: props.clientId,
        listAll: true
    })
        .then((response) => {
            data.files = response.files;
        })
        .catch((err) => {
            ToastUtil.error(err);
        })
        .finally(() => {
            data.loading = false;
        })
}

const getFilenames = () => {
    data.loading = true;
    ApiWrapper.post("client-files", 'get-filenames', {})
        .then((response) => {
            if (response.filenames) {
                data.filenames = response.filenames.map(function (obj) {
                    return obj.filename
                })
            }
            data.loading = false;
        })
        .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
        })
}

const editFileName = (file) => {
    data.loading = true;
    ApiWrapper.post("client-files", "edit", {
        editedItem: file,
    })
        .then((response) => {
            data.loading = false;
            fetchData();
            ToastUtil.successMsg('Updated filename');
        })
        .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
        })
}

const formatTimestampByLocale = (timestamp) => {
    const date = new Date(timestamp)
    const timeOptions = {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
    }
    const dateOptions = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZoneName: "short"
    }
    return date.toLocaleString('en', {...dateOptions, ...timeOptions})
}

const removeFile = (file) => {
    data.loading = true;
    ApiWrapper.post("client-files", "delete-file", {
        id: file.id,
        key: file.key,
    })
        .then((response) => {
            data.files = data.files.filter((f) => {
                return f.fileid !== file.fileid;
            });
            activeComponentStore.notifyComponents(['files'], compName);
            ToastUtil.successMsg('Deleted file');
        })
        .catch((err) => {
            ToastUtil.error(err);
        })
        .finally(() => {
            data.loading = false;
        })
}

const setFileAttributes = (file) => {
    data.loading = true;
    ApiWrapper.post("client-files", "set-file-attributes", {
        fileId: file.id,
        clientId: props.clientId,
        metadata: file.metadata
    })
        .then((response) => {
            data.files = response.files;
            ToastUtil.successMsg('Updated file');
        })
        .catch((err) => {
            ToastUtil.error(err);
        })
        .finally(() => {
            data.loading = false;
        });
}

const windowOpen = (file) => {
    if (Permissions.checkPermissions(['files.download'])) {
        ApiWrapper.post("client-files", "download-file", {key: file.key})
            .then((response) => {
                window.open(response.url, "_blank");
            })
            .catch((err) => {
                ToastUtil.error(err);
            });
    }
}

</script>

<template>
    <v-container fluid>
        <v-card>
            <v-toolbar flat color="white">
                <v-toolbar-title>Files</v-toolbar-title>
                <v-progress-linear :active="data.loading" :indeterminate="data.loading" absolute bottom color="primary"></v-progress-linear>
                <v-divider class="mx-4" inset vertical></v-divider>
            </v-toolbar>
            <v-list v-if="data.files.length!==0" flat>
                <template v-for="(file, index) in data.files"
                          :key="file.fileid"
                >
                    <v-list-item class="v-list-item--link" v-ripple>
                        <div class="files-container-columns flex-grow">
                            <div class="files-container-rows flex-grow">
                                <div @click="windowOpen(file)" class="flex-grow" v-if="file.isEditing !== true">
                                    <v-list-item-subtitle font-weight-medium>
                                        {{ file.filename }} - {{ formatTimestampByLocale(file.created_at) }} - {{ getTimezone(timezone) }}<span
                                        v-if="file.uploadedBy !== '' && file.uploadedBy != null">. Uploaded by {{ file.uploadedby }}</span>
                                    </v-list-item-subtitle>
                                </div>
                                <div class="flex-grow files-container-columns flex-grow" v-if="file.isEditing !== true">
                                    <v-checkbox
                                        @change="Permissions.checkPermissions(['files.set-attributes']) ? setFileAttributes(file) : true"
                                        class="mr-4 flex-shrink"
                                        label="For Admins"
                                        v-model="file.metadata.isForAdmins">
                                    </v-checkbox>
                                    <v-checkbox
                                        @change="Permissions.checkPermissions(['files.set-attributes']) ? setFileAttributes(file) : true"
                                        class="mr-4 flex-shrink"
                                        label="For Adjusters"
                                        v-model="file.metadata.isForAdjusters">
                                    </v-checkbox>
                                </div>
                                <div class="flex-grow" v-if="file.isEditing !== true">
                                    <v-autocomplete
                                        label="Display Name"
                                        @update:modelValue="file.metadata.displayName=$event; Permissions.checkPermissions(['files.set-attributes']) ? setFileAttributes(file) : true"
                                        v-model="file.metadata.displayName"
                                        :items="data.filenames"
                                        class="flex-grow"
                                    >
                                    </v-autocomplete>
                                </div>
                                <div class="flex-grow" v-if="file.isEditing">
                                    <v-text-field
                                        v-model="file.filename"
                                        label="Edit File Name"
                                        hide-details="auto"
                                        class="flex-grow"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="files-container-columns flex-shrink">
                                <div class="files-container-columns flex-shrink" v-if="file.isEditing !== true">
                                    <v-list-item-action class="ml-2 flex-shrink">
                                        <v-btn
                                            icon="mdi-pencil"
                                            v-if="Permissions.checkPermissions(['files.update'])"
                                            @click="file.isEditing = true; data.originalFileName = file.filename"
                                        >
                                        </v-btn>
                                    </v-list-item-action>
                                    <v-list-item-action class="ml-2 mr-2 flex-shrink">
                                        <v-btn
                                            icon="mdi-delete"
                                            v-if="Permissions.checkPermissions(['files.delete'])"
                                            @click.stop="removeFile(file)"
                                        >
                                        </v-btn>
                                    </v-list-item-action>
                                </div>
                                <div class="files-container-columns" v-if="file.isEditing">
                                    <v-list-item-action class="ml-2 flex-shrink">
                                        <v-btn
                                            size="x-small"
                                            variant="elevated"
                                            color="primary"
                                            @click="editFileName(file); file.isEditing = false"
                                        >
                                            Save
                                        </v-btn>
                                    </v-list-item-action>
                                    <v-list-item-action class="ml-2 mr-2 flex-shrink">
                                        <v-btn
                                            size="x-small"
                                            variant="elevated"
                                            color="error"
                                            @click="file.isEditing = false; file.filename = data.originalFileName"
                                        >
                                            Cancel
                                        </v-btn>
                                    </v-list-item-action>
                                </div>
                            </div>
                        </div>
                    </v-list-item>

                    <v-divider
                        v-if="index + 1 < data.files.length"
                        :key="index"
                    ></v-divider>
                </template>
            </v-list>
            <v-list v-else>
                <v-list-item>
                    No Files Uploaded
                </v-list-item>
            </v-list>
        </v-card>
    </v-container>
</template>

<style scoped>
.container {
    padding: 5px;
}
</style>